import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    IconButton,
    Tooltip,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import { FeatureCheckboxListProps, FeaturesMetaData } from "./interface";
import { useCallback, useEffect, useMemo, useState, type ElementType } from "react";
import { ExpandMore } from "@material-ui/icons";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import ProjectModuleMetaField from "./meta-data-controller/ProjectModuleMetaField";
import TceFireIntegrationMetaField from "./meta-data-controller/TceFireIntegrationMetaField";
import DynamicNotificationTimeMetaField from "./meta-data-controller/DynamicNotificationTimeMetaField";
import { FEATURE_KEYS_LIST, FEATURE_KEY_TO_NAME_MAP } from "./constants";

const useStyles = makeStyles({
    featureList: {
        marginTop: "30px",
        marginBottom: "30px",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        overflowY: "auto",
        position: "relative",
        height: "680px",
        padding: "10px 10px",
        boxShadow: "1px 1px 5px black",
    },
    accordion: {
        width: "47%",
        marginBottom: "15px",
    },
    accordionSummary: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    },
    labelGroup: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    infoButton: {
        padding: "2px",
    },
});

const FeatureCheckboxList = ({
    featureState,
    handleFeatureStateChange,
    handleFeaturesMetaDataChange,
    selectedCompany,
    selectedCompanyMetaData,
}: FeatureCheckboxListProps) => {
    const featuresMetaDataInitialState: FeaturesMetaData = useMemo(() => {
        return {
            PROJ_MODULE: {
                allowedRolesForJobCosting: [],
            },
            DYNAMIC_NOTIFICATION_TIME: {
                invoice: "04:30",
                assignment: "04:00",
                ticket: "04:00",
                service_agreement: "04:00",
            },
            TCE_FIRE_INTEGRATION: {
                letUserDisconnect: true,
            },
        };
    }, []);
    const classes = useStyles();
    const editableFeaturesList = useMemo(
        () => [
            "PROJ_MODULE",
            "DYNAMIC_NOTIFICATION_TIME",
            "TCE_FIRE_INTEGRATION",
        ],
        []
    );
    const [featuresMetaData, setFeaturesMetaData] = useState<FeaturesMetaData>(
        selectedCompanyMetaData
    );

    const updateProjectModuleMetaData = useCallback(
        (selectedRoles: string[]) => {
            setFeaturesMetaData((prevState: FeaturesMetaData) => {
                return {
                    ...prevState,
                    PROJ_MODULE: {
                        allowedRolesForJobCosting: selectedRoles,
                    },
                };
            });
        },
        [setFeaturesMetaData]
    );

    const updateTceFireIntegrationMetaData = useCallback(
        (letUserDisconnect: boolean) => {
            setFeaturesMetaData((prevState: FeaturesMetaData) => {
                return {
                    ...prevState,
                    TCE_FIRE_INTEGRATION: {
                        letUserDisconnect,
                    },
                };
            });
        },
        [setFeaturesMetaData]
    );

    const updateDynamicNotificationTimeMetaData = useCallback(
        (dynamicNotificationTime: FeaturesMetaData["DYNAMIC_NOTIFICATION_TIME"]) => {
            setFeaturesMetaData((prevState: FeaturesMetaData) => {
                return {
                    ...prevState,
                    DYNAMIC_NOTIFICATION_TIME: {
                        ...dynamicNotificationTime,
                    },
                };
            });
        },
        [setFeaturesMetaData]
    );

    useEffect(() => {
        handleFeaturesMetaDataChange(featuresMetaData);
    }, [featuresMetaData, handleFeaturesMetaDataChange]);

    useEffect(() => {
        setFeaturesMetaData(featuresMetaDataInitialState);
    }, [selectedCompany, setFeaturesMetaData, featuresMetaDataInitialState]);

    useEffect(() => {
        setFeaturesMetaData({
            ...featuresMetaDataInitialState,
            ...selectedCompanyMetaData,
        });
    }, [selectedCompanyMetaData, setFeaturesMetaData, featuresMetaDataInitialState]);

    const featureDescriptions: Record<string, string> = useMemo(
        () => ({
            ESTIMATE_REJECTION_REASON:
                "Enables estimate rejection analysis and allows custom rejection reasons to be created. You can add the reasons into the Estimate Rejection Reasons section.",
            DYNAMIC_NOTIFICATION_TIME:
                "By default, reminder notifications run at 11:30 UTC. Use this setting to choose a custom UTC time (HH:mm) for each module.",
            PRICE_BOOK:
                "Enables the Price Book module. WARNING: If this company has Flat Rate Items that need to be available in Price Book, they must be migrated to Price Book Items BEFORE enabling. Migration will NOT be possible after Price Book is enabled.",
            ADD_LINE_ITEMS_TO_DESC:
                "Adds estimate line items along with item description to the ticket description when transferring an independent estimate to a new ticket.",
            INDV_LINE_ITEM_TAX:
                "Enable individual line item taxation for invoice/estimate.",
            GOOGLE_CALENDAR:
                "Enable Google Calendar integration.",
            PERIODIC_LOCATION_TRACKING:
                "Enable periodic location tracking for technicians in real time.",
        }),
        []
    );

    const featureTooltipIcons: Record<string, ElementType> = useMemo(
        () => ({
            DYNAMIC_NOTIFICATION_TIME: VisibilityOutlinedIcon,
        }),
        []
    );

    return (
        <div className={classes.featureList}>
            {FEATURE_KEYS_LIST.map((featureKey, index) => (
                <Accordion
                    className={classes.accordion}
                    defaultExpanded={featureState[featureKey]}
                    expanded={
                        featureState[featureKey] &&
                        editableFeaturesList.includes(featureKey)
                    }
                    key={index}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        className={classes.accordionSummary}
                    >
                        <div className={classes.accordionSummary}>
                            <div className={classes.labelGroup}>
                                <p style={{ display: "inline" }}>
                                    {FEATURE_KEY_TO_NAME_MAP[featureKey]}
                                </p>
                                {featureDescriptions[featureKey] && (() => {
                                    const TooltipIcon =
                                        featureTooltipIcons[featureKey] ||
                                        InfoOutlinedIcon;
                                    return (
                                        <Tooltip
                                            title={
                                                featureDescriptions[featureKey]
                                            }
                                        >
                                            <IconButton
                                                className={classes.infoButton}
                                                size="small"
                                            >
                                                <TooltipIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                            <Checkbox
                                key={featureKey}
                                checked={featureState[featureKey]}
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    handleFeatureStateChange(
                                        event.target.checked,
                                        featureKey
                                    );
                                }}
                                inputProps={{ "aria-label": "controlled" }}
                            />
                        </div>
                    </AccordionSummary>

                    <AccordionDetails>
                        {editableFeaturesList.includes(featureKey) &&
                            featureKey === "PROJ_MODULE" && (
                                <ProjectModuleMetaField
                                    optionsSelectedHandler={
                                        updateProjectModuleMetaData
                                    }
                                    featuresMetaData={featuresMetaData}
                                />
                            )}
                        {editableFeaturesList.includes(featureKey) &&
                            featureKey === "TCE_FIRE_INTEGRATION" && (
                                <TceFireIntegrationMetaField
                                    optionsSelectedHandler={
                                        updateTceFireIntegrationMetaData
                                    }
                                    featuresMetaData={featuresMetaData}
                                />
                            )}
                        {editableFeaturesList.includes(featureKey) &&
                            featureKey === "DYNAMIC_NOTIFICATION_TIME" && (
                                <DynamicNotificationTimeMetaField
                                    optionsSelectedHandler={
                                        updateDynamicNotificationTimeMetaData
                                    }
                                    featuresMetaData={featuresMetaData}
                                />
                            )}
                    </AccordionDetails>
                </Accordion>
            ))}
        </div>
    );
};

export default FeatureCheckboxList;
